import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { notifyError } from "../lib/ui";
import type { Condition, Product } from "../types";
import { CATEGORY_LABELS } from "../types";

const CONDITIONS: { value: Condition; label: string }[] = [
  { value: "excellent", label: "Отличное" },
  { value: "good",      label: "Хорошее"  },
  { value: "fair",      label: "Удовлетв." },
];

const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL",
               "36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "47",
               "One size"];

interface PhotoSlot {
  preview: string;   // objectURL для предпросмотра
  fileId: string;    // local:uuid.ext после загрузки
  uploading: boolean;
  error: string | null;
}

export default function AddProductPage() {
  const nav = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const [photos, setPhotos] = useState<PhotoSlot[]>([]);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<string>("tshirt");
  const [customCat, setCustomCat] = useState(""); // ручная категория при выборе «Другое»
  const [size, setSize] = useState("");
  const [price, setPrice] = useState("");
  const [condition, setCondition] = useState<Condition>("good");
  const [busy, setBusy] = useState(false);

  // Автоопределение категории по названию.
  const handleTitleChange = (v: string) => {
    setTitle(v);
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const newSlots: PhotoSlot[] = Array.from(files).map((f) => ({
      preview: URL.createObjectURL(f),
      fileId: "",
      uploading: true,
      error: null,
    }));

    setPhotos((prev) => [...prev, ...newSlots]);
    const startIdx = photos.length;

    // Загружаем каждый файл по отдельности, обновляем слот по индексу.
    await Promise.all(
      Array.from(files).map(async (file, i) => {
        const idx = startIdx + i;
        try {
          const form = new FormData();
          form.append("files", file);

          const initData = window.Telegram?.WebApp?.initData ?? "";
          const res = await fetch("/api/v1/products/upload-photo", {
            method: "POST",
            headers: { Authorization: `tma ${initData}` },
            body: form,
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({ detail: "Ошибка загрузки" }));
            throw new Error(err.detail ?? "Ошибка загрузки");
          }
          const { file_ids } = await res.json();
          setPhotos((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], fileId: file_ids[0], uploading: false };
            return next;
          });
        } catch (e) {
          setPhotos((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], uploading: false, error: (e as Error).message };
            return next;
          });
        }
      })
    );
  };

  const removePhoto = (idx: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const movePhoto = (idx: number, dir: -1 | 1) => {
    setPhotos((prev) => {
      const next = [...prev];
      const to = idx + dir;
      if (to < 0 || to >= next.length) return prev;
      [next[idx], next[to]] = [next[to], next[idx]];
      return next;
    });
  };

  const categoryValue = category === "__custom__" ? customCat.trim() : category;
  const allUploaded = photos.length > 0 && photos.every((p) => !p.uploading && p.fileId);
  const canSubmit = allUploaded && categoryValue && size && price && !busy;

  const submit = async () => {
    setBusy(true);
    try {
      const [main, ...extra] = photos.map((p) => p.fileId);
      await api<Product>("/products", {
        method: "POST",
        body: JSON.stringify({
          file_id: main,
          extra_file_ids: extra.length ? extra : undefined,
          title: title || null,
          category: categoryValue,
          size,
          price: Number(price),
          condition,
        }),
      });
      nav("/manager");
    } catch (e) {
      notifyError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container">
      <h1>Новый товар</h1>

      {/* Зона загрузки фото */}
      <div style={{ marginBottom: 16 }}>
        <label className="micro" style={{ display: "block", color: "var(--hint)", marginBottom: 8 }}>
          Фотографии (первая — главная)
        </label>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
          {photos.map((p, i) => (
            <div key={i} style={{ position: "relative", width: 90, height: 90 }}>
              <img
                src={p.preview}
                alt=""
                style={{
                  width: 90, height: 90,
                  objectFit: "cover",
                  borderRadius: 10,
                  opacity: p.uploading ? 0.5 : 1,
                  border: i === 0 ? "2px solid var(--accent)" : "2px solid var(--border)",
                }}
              />
              {p.uploading && (
                <div style={{
                  position: "absolute", inset: 0, display: "flex",
                  alignItems: "center", justifyContent: "center",
                  fontSize: 20, borderRadius: 10, background: "rgba(0,0,0,0.3)",
                }}>⏳</div>
              )}
              {p.error && (
                <div style={{
                  position: "absolute", inset: 0, display: "flex",
                  alignItems: "center", justifyContent: "center",
                  fontSize: 11, color: "red", textAlign: "center", padding: 4,
                  background: "rgba(0,0,0,0.6)", borderRadius: 10,
                }}>
                  {p.error}
                </div>
              )}
              {!p.uploading && (
                <div style={{
                  position: "absolute", bottom: 2, left: 0, right: 0,
                  display: "flex", justifyContent: "center", gap: 3,
                }}>
                  {i > 0 && (
                    <button onClick={() => movePhoto(i, -1)} style={arrowBtn}>‹</button>
                  )}
                  {i < photos.length - 1 && (
                    <button onClick={() => movePhoto(i, 1)} style={arrowBtn}>›</button>
                  )}
                  <button onClick={() => removePhoto(i)} style={{ ...arrowBtn, background: "rgba(180,0,0,0.8)" }}>✕</button>
                </div>
              )}
              {i === 0 && (
                <span className="micro" style={{
                  position: "absolute", top: 3, left: 3,
                  background: "var(--accent)", color: "#fff",
                  fontSize: 9, letterSpacing: "0.06em", padding: "2px 5px 1px", borderRadius: 4,
                }}>
                  ГЛАВНОЕ
                </span>
              )}
            </div>
          ))}

          {/* Кнопка добавить */}
          <button
            onClick={() => inputRef.current?.click()}
            style={{
              width: 90, height: 90,
              border: "2px dashed var(--border)",
              borderRadius: 10,
              background: "var(--secondary-bg)",
              color: "var(--hint)",
              fontSize: 28,
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            +
          </button>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={(e) => handleFiles(e.target.files)}
        />

        <p style={{ fontSize: 12, color: "var(--hint)", margin: 0 }}>
          Нажми + чтобы выбрать фото из галереи или сделать снимок.
          Первое фото — главное. Порядок можно менять стрелками.
        </p>
      </div>

      <div className="field">
        <label>Название</label>
        <input
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="Например: Куртка Levi's vintage 90's"
        />
      </div>

      <div className="field">
        <label>Категория</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
          <option value="__custom__">✏️ Другое…</option>
        </select>
        {category === "__custom__" && (
          <input
            value={customCat}
            onChange={(e) => setCustomCat(e.target.value)}
            placeholder="Например: Ремни"
            style={{ marginTop: 8 }}
          />
        )}
      </div>

      <div className="row">
        <div className="field">
          <label>Размер</label>
          {/* Поле-комбо: можно выбрать из пресетов или ввести вручную
              (напр. длину ремня «110 см» — там не размер). */}
          <input
            list="size-options"
            value={size}
            onChange={(e) => setSize(e.target.value)}
            placeholder="S, M, L… или вручную"
          />
          <datalist id="size-options">
            {SIZES.map((s) => <option key={s} value={s} />)}
          </datalist>
        </div>
        <div className="field">
          <label>Цена ₽</label>
          <input
            type="number" inputMode="numeric"
            value={price} onChange={(e) => setPrice(e.target.value)}
          />
        </div>
      </div>

      <div className="field">
        <label>Состояние</label>
        <select value={condition} onChange={(e) => setCondition(e.target.value as Condition)}>
          {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>

      <button className="btn" onClick={submit} disabled={!canSubmit}>
        {busy ? "Публикуем…" : "Опубликовать"}
      </button>
    </div>
  );
}

const arrowBtn: React.CSSProperties = {
  background: "rgba(0,0,0,0.6)",
  color: "#fff",
  border: "none",
  borderRadius: 4,
  width: 22, height: 22,
  fontSize: 14,
  cursor: "pointer",
  padding: 0,
  display: "flex", alignItems: "center", justifyContent: "center",
};
